import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import CryptoJS from "crypto-js";
import {KEY, loginState} from "../../Constants";
import {dashboardApi} from "../../services/api";

// Material-UI Components
import {
    Snackbar,
    Stack,
    Alert,
    Backdrop,
    CircularProgress,
    useMediaQuery,
    Chip,
    Box,
    Typography,
    Paper,
    Fade
} from "@mui/material";
import {
    Business as BusinessIcon,
} from "@mui/icons-material";

// Custom Components
import FeaturedInfo from "../../components/featuredInfo/FeaturedInfo";
import WidgetLg from "../../components/widgetLG/WidgetLg";
import ProgressBars from "../../components/ProgressBars";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import OnboardingStepOne from "../../components/customDialogs/onboardingDialog";
import SubscriptionModal from "../../components/SubscriptionModal/SubscriptionModal";

// CSS
import "./home.css";

// Helper function for safe decryption
const safeDecrypt = (key) => {
    try {
        const encrypted = localStorage.getItem(key);
        if (!encrypted) return null;
        const decrypted = CryptoJS.AES.decrypt(encrypted, KEY).toString(CryptoJS.enc.Utf8);
        if (!decrypted) return null;
        JSON.parse(decrypted); // Validate JSON
        return decrypted;
    } catch (error) {
        console.error(`Failed to decrypt ${key}:`, error);
        return null;
    }
};

const apiFetch = async (url, options) => {
    try {
        const response = await fetch(url, options);
        if (!response.ok) throw new Error("API request failed");
        return await response.json();
    } catch (error) {
        console.error(error);
        return null;
    }
};

// Generate year options (current year plus 10 years back and 2 years forward)
const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 10; year <= currentYear + 2; year++) {
        years.push(year);
    }
    return years;
};

const HomeV2 = ({refreshSideBar, topBar}) => {
    const isMobile = useMediaQuery("(max-width:768px)");
    const location = useLocation();
    const navigate = useNavigate();

    // New state for year and month selection
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [isDataLoading, setIsDataLoading] = useState(false);

    const [subscription, setSubscription] = useState(safeDecrypt("subscriptionDetails"));
    let [originalText, setOriginalText] = useState(safeDecrypt("myMap"));
    const [, setOriginalText2] = useState(safeDecrypt("portfolioDetails"));

    const [set, updateSet] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState();
    const [loading,] = useState(false);

    const [subscriptionModal, setSubscriptionModal] = useState(false);
    const [dateSubscriptionExpired, setDateSubscriptionExpired] = useState(null);
    const [, setDateSubscribed] = useState(null);
    const [, setEmailAddress] = useState(null);
    const [subscriptionAmount, setSubscriptionAmount] = useState(0);
    const [status,] = useState(localStorage.getItem("isNew"));

    const getUserDetails = async (userDetails, month = null, year = null) => {
        setIsDataLoading(true);

        // Add month and year to the request if provided
        const requestBody = {
            ...userDetails,
            ...(month && {month}),
            ...(year && {year})
        };

        try {
            const apiResponse = await dashboardApi.getDetails(requestBody);

            if (apiResponse.ok) {
                const response = apiResponse.data;
                const map1 = new Map(Object.entries(response));
                const details = new Map(Object.entries(userDetails));
                const subscription = new Map();

                subscription.set("subscription_expired", details.get("subscription_expired"));
                subscription.set("subscription_amount", details.get("subscription_amount"));

                const myMap = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(details.entries())),
                    KEY
                ).toString();
                const portfolioDetails = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(map1.entries())),
                    KEY
                ).toString();
                const subscriptionDetails = CryptoJS.AES.encrypt(
                    JSON.stringify(Array.from(subscription.entries())),
                    KEY
                ).toString();

                localStorage.setItem("myMap", myMap);
                localStorage.setItem("isNew", userDetails.new);
                localStorage.portfolioDetails = portfolioDetails;
                localStorage.subscriptionDetails = subscriptionDetails;

                setOriginalText(myMap);
                setOriginalText2(portfolioDetails);
                setSubscription(subscriptionDetails);

                const periodText = month && year ? `${getMonthName(month)} ${year}` : 'current period';

                showSnackbar({
                    title: "Success!",
                    message: `Dashboard details updated for ${periodText}`,
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                refreshSideBar();
                topBar();
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Failed to update dashboard details",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
        } catch (error) {
            console.error(error);
            showSnackbar({
                title: "Error!",
                message: "Failed to update dashboard details",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        } finally {
            setIsDataLoading(false);
        }
    };

    // Helper function to get month name
    const getMonthName = (monthNumber) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[monthNumber - 1];
    };

    useEffect(() => {
        const userDetails = location.state?.userDetails || (() => {
            const decrypted = safeDecrypt("myMap");
            if (decrypted) {
                try {
                    return Object.fromEntries(JSON.parse(decrypted));
                } catch {
                    return null;
                }
            }
            return null;
        })();

        if (userDetails) {
            getUserDetails(userDetails, selectedMonth, selectedYear);
        } else {
            navigate("/");
            showSnackbar({
                title: "Error!",
                message: "No user details found. Please log in.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }

        let userDetailsArray = [];
        try {
            if (originalText) {
                userDetailsArray = JSON.parse(originalText);
            }
        } catch (error) {
            console.error("Failed to parse originalText:", error);
        }

        let date_subscribed, email_address;
        if (Array.isArray(userDetailsArray) && userDetailsArray.length > 0) {
            userDetailsArray.forEach(([key, value]) => {
                if (key === "date_subscription_renewed") {
                    date_subscribed = new Date(value);
                }

                if (key === "subscription_expires_at") {
                    setDateSubscriptionExpired(new Date(value))
                }

                if (key === "email_address") {
                    setEmailAddress(value);
                }
            });
        }

        setDateSubscribed(date_subscribed);

        if (status !== "yes") {
            try {
                const subscriptionArray = subscription ? JSON.parse(subscription) : [];
                const subscriptionExpired = subscriptionArray[0]?.[1] || false;

                if (!date_subscribed) {
                    setDateSubscriptionExpired("First subscription");
                }


                if (subscriptionExpired && status !== "yes") {
                    const amount = Math.ceil(subscriptionArray[1]?.[1] || 0);
                    setSubscriptionAmount(amount);
                    setSubscriptionModal(true);
                }
            } catch (e) {
                console.error("Subscription parsing error:", e);
            }
        }

        updateSet(true);
    }, []);

    const [open, setOpen] = useState(loginState);

    const handleClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpen(false);
    };

    let map = new Map();
    try {
        if (originalText && typeof originalText === 'string') {
            // Check if it's valid JSON and not still encrypted
            const parsed = JSON.parse(originalText);
            if (Array.isArray(parsed)) {
                map = new Map(parsed);
            }
        }
    } catch (e) {
        console.error("Failed to create map from originalText:", e);
        // Only redirect if data appears to be encrypted (starts with U2FsdGVk which is base64 for "Salted")
        // This prevents redirect loops while catching actual encryption issues
    }

    const handleClick = (message) => {
        setMessage(message.text);
        setSeverity(message.type);
        setOpen(true);
    };

    const viewTopBarClick = () => {
        topBar();
    };

    return (
        <>
            <Backdrop open={loading || isDataLoading} sx={{zIndex: (theme) => theme.zIndex.drawer + 1}}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            <SubscriptionModal
                open={subscriptionModal}
                onClose={() => setSubscriptionModal(false)}
                subscriptionAmount={subscriptionAmount}
                expired={true}
                expiryDate={dateSubscriptionExpired}
            />

            {set && (
                status === "yes" ? (
                    <div className="home">
                        <Stack spacing={2} sx={{width: "100%"}}>
                            <Snackbar open={open} autoHideDuration={2000} onClose={handleClose}>
                                <Alert onClose={handleClose} severity={severity} sx={{width: "100%"}}>
                                    {message}
                                </Alert>
                            </Snackbar>
                        </Stack>
                        <OnboardingStepOne details={map} snackBar={handleClick} topBar={viewTopBarClick}/>
                    </div>
                ) : (
                    <div className={subscriptionModal ? "home blur" : "home"}>
                        {/* Header Section with Property Label and Period Selector */}
                        <Fade in={true} timeout={800}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    backgroundColor: '#fafbfc',
                                    borderRadius: 3,
                                    border: '1px solid #e8eaed',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        gap: 1.5,
                                    }}
                                >
                                    {/* Left side - Property Label */}
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        <Chip
                                            icon={<BusinessIcon sx={{color: "#398DD2 !important", fontSize: 16}}/>}
                                            label="For All Properties"
                                            size="small"
                                            sx={{
                                                backgroundColor: "#f0f8ff",
                                                color: "#398DD2",
                                                fontWeight: 500,
                                                fontSize: isMobile ? "0.75rem" : "0.8rem",
                                                height: 28,
                                                '& .MuiChip-icon': {
                                                    color: "#398DD2",
                                                },
                                                border: "1px solid #398DD240",
                                                borderRadius: 2,
                                            }}
                                        />

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: "#999",
                                                fontStyle: "italic",
                                                fontSize: "0.7rem",
                                            }}
                                        >
                                            Showing data for{" "}
                                            <span style={{color: "#398DD2", fontWeight: 500}}>
                                            {getMonthName(selectedMonth)} {selectedYear}
                                        </span>
                                        </Typography>
                                    </Box>

                                    {/* Right side - Period Selectors */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            backgroundColor: 'white',
                                            borderRadius: 2,
                                            border: '1px solid #e0e0e0',
                                            px: 1.5,
                                            py: 0.5,
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        }}
                                    >
                                        {/* Period Label */}
                                        {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <CalendarIcon sx={{ color: "#666", fontSize: 16 }} />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: "#666",
                                                    fontWeight: 500,
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Period:
                                            </Typography>
                                        </Box>

                                         Month Selector
                                        <FormControl
                                            variant="standard"
                                            size="small"
                                            sx={{
                                                minWidth: isMobile ? 80 : 90,
                                                '& .MuiInput-underline:before': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:after': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                                    borderBottom: 'none',
                                                },
                                            }}
                                        >
                                            <Select
                                                value={selectedMonth}
                                                onChange={handleMonthChange}
                                                disabled={isDataLoading}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: '#398DD2',
                                                    '& .MuiSelect-select': {
                                                        padding: '2px 4px',
                                                        '&:focus': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        color: '#398DD2',
                                                        fontSize: 16,
                                                    },
                                                }}
                                            >
                                                {monthOptions.map((month) => (
                                                    <MenuItem key={month.value} value={month.value} sx={{ fontSize: '0.8rem' }}>
                                                        {isMobile ? month.label.slice(0, 3) : month.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Typography sx={{ color: "#ddd", fontSize: "0.9rem", mx: 0.5 }}>|</Typography>

                                         Year Selector
                                        <FormControl
                                            variant="standard"
                                            size="small"
                                            sx={{
                                                minWidth: isMobile ? 60 : 70,
                                                '& .MuiInput-underline:before': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:after': {
                                                    borderBottom: 'none',
                                                },
                                                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                                                    borderBottom: 'none',
                                                },
                                            }}
                                        >
                                            <Select
                                                value={selectedYear}
                                                onChange={handleYearChange}
                                                disabled={isDataLoading}
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600,
                                                    color: '#398DD2',
                                                    '& .MuiSelect-select': {
                                                        padding: '2px 4px',
                                                        '&:focus': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        color: '#398DD2',
                                                        fontSize: 16,
                                                    },
                                                }}
                                            >
                                                {yearOptions.map((year) => (
                                                    <MenuItem key={year} value={year} sx={{ fontSize: '0.8rem' }}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>*/}
                                    </Box>
                                </Box>

                                {/* Selected Period Display */}
                                {/*<Box sx={{ mt: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#999",
                                            fontStyle: "italic",
                                            fontSize: "0.7rem",
                                        }}
                                    >
                                        Showing data for{" "}
                                        <span style={{ color: "#398DD2", fontWeight: 500 }}>
                                            {getMonthName(selectedMonth)} {selectedYear}
                                        </span>
                                    </Typography>
                                </Box>*/}
                            </Paper>
                        </Fade>

                        {/* Main Dashboard Content */}
                        <Fade in={!isDataLoading} timeout={600}>
                            <div>
                                <FeaturedInfo/>
                                <ProgressBars/>
                                <WidgetLg/>
                            </div>
                        </Fade>
                    </div>
                )
            )}
        </>
    );
};

export default HomeV2;